import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Button from './Button';
import QuestionMarkCircleIcon from './icons/QuestionMarkCircleIcon';
import EnvelopeIcon from './icons/EnvelopeIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { useTranslation } from 'react-i18next';

interface HelpMenuProps {
  onHelperClick: () => void;
}

const HelpMenu: React.FC<HelpMenuProps> = ({ onHelperClick }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleContactClick = () => {
    window.location.href = 'mailto:podrska@klikero.com';
    setIsOpen(false);
  };

  const handleHelperClick = () => {
    onHelperClick();
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuRef]);

  return (
    <div ref={menuRef} className="relative">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        title={t('helpMenu.title')}
        variant="ghost"
        size="base"
        className="font-medium space-x-2"
        aria-label={t('helpMenu.ariaLabel')}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <QuestionMarkCircleIcon className="w-5 h-5 text-sky-600" />
        <span>{t('helpMenu.help')}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          >
            <div className="py-1">
              <Button
                onClick={handleHelperClick}
                variant="ghost"
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center space-x-3"
              >
                <QuestionMarkCircleIcon className="w-5 h-5 text-slate-500" />
                <span>{t('helpMenu.questions')}</span>
              </Button>
              <Button
                onClick={handleContactClick}
                variant="ghost"
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center space-x-3"
              >
                <EnvelopeIcon className="w-5 h-5 text-slate-500" />
                <span>{t('helpMenu.contact')}</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HelpMenu;
